// ============================================================================
// Type - Structured type representation
// ============================================================================
// This module defines a structured, object-oriented type system to replace
// ============================================================================

/**
 * Base Type class - all types inherit from this
 */
export class Type {
  constructor() {}
  
  /**
   * Check if this type equals another type
   * @param {Type} other - Type to compare with
   * @returns {boolean}
   */
  equals(other) {
    return this.toString() === other.toString();
  }
  
  /**
   * Convert type to string representation (for debugging/display)
   * @returns {string}
   */
  toString() {
    throw new Error('toString() must be implemented by subclasses');
  }
  
  /**
   * Check if this type is compatible with another type
   * @param {Type} target - Target type
   * @param {TypeAliasMap} aliases - Type aliases
   * @returns {boolean}
   */
  isCompatibleWith(target, aliases) {
    // When the target is an intersection, the value must be compatible with the
    // merged type (for all-object intersections) or with every constituent.
    if (target instanceof IntersectionType) {
      const merged = target.merge(aliases);
      if (merged) {
        return this.isCompatibleWith(merged, aliases);
      }
      return target.types.every(t => this.isCompatibleWith(t, aliases));
    }
    // Default: check equality
    return this.equals(target) || (target instanceof PrimitiveType && target.name === 'any');
  }
}

/**
 * Primitive types: string, number, boolean, null, undefined, any, never
 */
export class PrimitiveType extends Type {
  constructor(name) {
    super();
    this.kind = 'primitive';
    this.name = name; // 'string', 'number', 'boolean', 'null', 'undefined', 'any', 'never'
  }
  
  toString() {
    return this.name;
  }
  
  equals(other) {
    return other instanceof PrimitiveType && this.name === other.name;
  }
  
  isCompatibleWith(target, aliases) {
    if ((target instanceof PrimitiveType && target.name === 'any') || 
        (this instanceof PrimitiveType && this.name === 'any')) return true;
    if (this instanceof PrimitiveType && this.name === 'never') return true;
    if (target instanceof PrimitiveType && target.name === 'never') return false;
    
    // Check if target is a union containing this type
    if (target instanceof UnionType) {
      return target.types.some(t => this.isCompatibleWith(t, aliases));
    }
    
    // Resolve aliases and type member access (e.g. State.dogPage)
    if (target instanceof TypeAlias || target instanceof TypeMemberAccess || target instanceof KeyofType) {
      const resolved = aliases.resolve(target);
      // Avoid infinite recursion if alias can't be resolved
      if (resolved === target) return false;
      return this.isCompatibleWith(resolved, aliases);
    }

    // Resolve keyof targets (e.g. keyof State → "a" | "b")
    if (target instanceof KeyofType) {
      const resolved = aliases.resolveKeyof(target);
      if (resolved === target) return false;
      return this.isCompatibleWith(resolved, aliases);
    }
    
    return this.equals(target);
  }
}

// Singleton instances for common primitives
export const AnyType = new PrimitiveType('any');
export const NeverType = new PrimitiveType('never');
export const StringType = new PrimitiveType('string');
export const NumberType = new PrimitiveType('number');
export const BooleanType = new PrimitiveType('boolean');
export const NullType = new PrimitiveType('null');
export const UndefinedType = new PrimitiveType('undefined');
export const VoidType = new PrimitiveType('void');

/**
 * Literal types: "hello", 42, true, false
 */
export class LiteralType extends Type {
  constructor(value, baseType) {
    super();
    this.kind = 'literal';
    this.value = value;
    this.baseType = baseType; // StringType, NumberType, or BooleanType
  }
  
  toString() {
    if (this.baseType === StringType) {
      return `"${this.value}"`;
    }
    return String(this.value);
  }
  
  equals(other) {
    return other instanceof LiteralType && 
           this.value === other.value && 
           this.baseType.equals(other.baseType);
  }
  
  isCompatibleWith(target, aliases) {
    if (target instanceof PrimitiveType && target.name === 'any') return true;

    // Allow typed arrays to match the generic array alias
    if (target instanceof TypeAlias && target.name === 'array') {
      return true;
    }
    
    // Literal is compatible with its base type
    if (this.baseType.equals(target)) return true;
    
    // Check if target is a union containing this literal or base type
    if (target instanceof UnionType) {
      return target.types.some(t => this.isCompatibleWith(t, aliases));
    }
    
    // Resolve aliases and type member access
    if (target instanceof TypeAlias || target instanceof TypeMemberAccess || target instanceof KeyofType) {
      const resolved = aliases.resolve(target);
      // Avoid infinite recursion if alias can't be resolved
      if (resolved === target) return false;
      return this.isCompatibleWith(resolved, aliases);
    }
    
    return this.equals(target);
  }
  
  getBaseType() {
    return this.baseType;
  }
}

/**
 * Array types: T[]
 */
export class ArrayType extends Type {
  constructor(elementType) {
    super();
    this.kind = 'array';
    this.elementType = elementType;
  }
  
  toString() {
    const elementStr = this.elementType.toString();
    if (this.elementType instanceof UnionType || this.elementType instanceof IntersectionType) {
      return `(${elementStr})[]`;
    }
    return `${elementStr}[]`;
  }
  
  equals(other) {
    return other instanceof ArrayType && this.elementType.equals(other.elementType);
  }
  
  isCompatibleWith(target, aliases) {
    if (target instanceof PrimitiveType && target.name === 'any') return true;

    // Allow typed arrays to match the generic array alias
    if (target instanceof TypeAlias && target.name === 'array') {
      return true;
    }
    
    // Resolve aliases and type member access
    if (target instanceof TypeAlias || target instanceof TypeMemberAccess || target instanceof KeyofType) {
      const resolved = aliases.resolve(target);
      // Avoid infinite recursion if alias can't be resolved
      if (resolved === target) return false;
      return this.isCompatibleWith(resolved, aliases);
    }
    
    // Check structural compatibility
    if (target instanceof ArrayType) {
      return this.elementType.isCompatibleWith(target.elementType, aliases);
    }
    
    // Check if target is a union containing array types
    if (target instanceof UnionType) {
      return target.types.some(t => this.isCompatibleWith(t, aliases));
    }
    
    return false;
  }
}

/**
 * Tuple types: [string, number, boolean]
 * Fixed-length sequence where each position has its own type.
 */
export class TupleType extends Type {
  /**
   * @param {Type[]} elements - Type of each positional element
   */
  constructor(elements) {
    super();
    this.kind = 'tuple';
    this.elements = elements; // Type[]
  }

  toString() {
    return `[${this.elements.map(t => t.toString()).join(', ')}]`;
  }

  equals(other) {
    if (!(other instanceof TupleType)) return false;
    if (this.elements.length !== other.elements.length) return false;
    return this.elements.every((el, i) => el.equals(other.elements[i]));
  }

  isCompatibleWith(target, aliases) {
    if (target instanceof PrimitiveType && target.name === 'any') return true;

    // Allow typed tuples to match the generic array alias
    if (target instanceof TypeAlias && target.name === 'array') return true;

    // Resolve aliases and type member access
    if (target instanceof TypeAlias || target instanceof TypeMemberAccess || target instanceof KeyofType) {
      const resolved = aliases.resolve(target);
      if (resolved === target) return false;
      return this.isCompatibleWith(resolved, aliases);
    }

    // Tuple is compatible with another tuple of the same length and element types
    if (target instanceof TupleType) {
      if (this.elements.length !== target.elements.length) return false;
      return this.elements.every((el, i) => el.isCompatibleWith(target.elements[i], aliases));
    }

    // A tuple [A, B] is assignable to (A | B)[] — i.e. a homogeneous array
    // whose element type covers every element of the tuple.
    if (target instanceof ArrayType) {
      return this.elements.every(el => el.isCompatibleWith(target.elementType, aliases));
    }

    // Check if target is a union containing compatible types
    if (target instanceof UnionType) {
      return target.types.some(t => this.isCompatibleWith(t, aliases));
    }

    return false;
  }

  /**
   * Return the type at a numeric index, or null for out-of-bounds.
   * @param {number} index
   * @returns {Type|null}
   */
  getElementType(index) {
    if (index < 0 || index >= this.elements.length) return null;
    return this.elements[index];
  }

  /**
   * The literal number type for the tuple's length.
   * @returns {LiteralType}
   */
  getLengthType() {
    return new LiteralType(this.elements.length, NumberType);
  }
}

/**
 * Object types: { name: string, age: number }
 */
export class ObjectType extends Type {
  constructor(properties = new Map(), name = null, indexSignature = null) {
    super();
    this.kind = 'object';
    this.properties = properties; // Map<string, {type: Type, optional: boolean}>
    this.name = name; // Optional class name for display (e.g. 'Router')
    this.indexSignature = indexSignature; // { keyType: Type, valueType: Type } | null
  }
  
  toString() {
    if (this.name) return this.name;
    const parts = [];
    if (this.indexSignature) {
      parts.push(`[key: ${this.indexSignature.keyType}]: ${this.indexSignature.valueType}`);
    }
    for (const [key, prop] of this.properties) {
      const optMarker = prop.optional ? '?' : '';
      parts.push(`${key}${optMarker}: ${prop.type.toString()}`);
    }
    if (parts.length === 0) return '{}';
    return `{${parts.join(', ')}}`;
  }
  
  equals(other) {
    if (!(other instanceof ObjectType)) return false;
    if (this.properties.size !== other.properties.size) return false;
    // Compare index signatures
    const thisHas = !!this.indexSignature;
    const otherHas = !!other.indexSignature;
    if (thisHas !== otherHas) return false;
    if (thisHas && otherHas) {
      if (!this.indexSignature.keyType.equals(other.indexSignature.keyType)) return false;
      if (!this.indexSignature.valueType.equals(other.indexSignature.valueType)) return false;
    }
    for (const [key, prop] of this.properties) {
      const otherProp = other.properties.get(key);
      if (!otherProp) return false;
      if (prop.optional !== otherProp.optional) return false;
      if (!prop.type.equals(otherProp.type)) return false;
    }
    return true;
  }
  
  isCompatibleWith(target, aliases) {
    if (target instanceof PrimitiveType && target.name === 'any') return true;

    // When target is an intersection, delegate to merged type or check all constituents
    if (target instanceof IntersectionType) {
      const merged = target.merge(aliases);
      if (merged) {
        return this.isCompatibleWith(merged, aliases);
      }
      return target.types.every(t => this.isCompatibleWith(t, aliases));
    }

    // Allow object types to match the generic object alias
    if (target instanceof TypeAlias && target.name === 'object') {
      return true;
    }
    
    // Resolve aliases and type member access
    if (target instanceof TypeAlias || target instanceof TypeMemberAccess || target instanceof KeyofType) {
      const resolved = aliases.resolve(target);
      // Avoid infinite recursion if alias can't be resolved
      if (resolved === target) return false;
      return this.isCompatibleWith(resolved, aliases);
    }
    
    // An ObjectType is compatible with a RecordType if all its property values
    // are compatible with the record's value type (structural index match)
    if (target instanceof RecordType) {
      // If source has an index signature, check its value type too
      if (this.indexSignature) {
        if (!this.indexSignature.valueType.isCompatibleWith(target.valueType, aliases)) {
          return false;
        }
      }
      for (const [, prop] of this.properties) {
        if (!prop.type.isCompatibleWith(target.valueType, aliases)) {
          return false;
        }
      }
      return true;
    }

    // Check structural compatibility
    if (target instanceof ObjectType) {
      // If target has an index signature, all source properties must be compatible with it
      if (target.indexSignature) {
        for (const [, prop] of this.properties) {
          if (!prop.type.isCompatibleWith(target.indexSignature.valueType, aliases)) {
            return false;
          }
        }
        if (this.indexSignature) {
          if (!this.indexSignature.valueType.isCompatibleWith(target.indexSignature.valueType, aliases)) {
            return false;
          }
        }
        return true;
      }
      // Check all required properties in target exist in this object
      for (const [key, targetProp] of target.properties) {
        if (!targetProp.optional && !this.properties.has(key)) {
          // If source has an index signature that covers this property, allow it
          if (this.indexSignature && this.indexSignature.valueType.isCompatibleWith(targetProp.type, aliases)) {
            continue;
          }
          return false; // Missing required property
        }
        
        const thisProp = this.properties.get(key);
        if (thisProp) {
          // Defensive check: ensure thisProp.type is a proper Type object
          if (!thisProp.type || typeof thisProp.type.isCompatibleWith !== 'function') {
            return false; // Invalid property type
          }
          if (!thisProp.type.isCompatibleWith(targetProp.type, aliases)) {
            return false; // Property type mismatch
          }
        }
      }
      
      return true;
    }
    
    // Check if target is a union containing object types
    if (target instanceof UnionType) {
      return target.types.some(t => this.isCompatibleWith(t, aliases));
    }
    
    return false;
  }
  
  /**
   * Get the type of a property
   * @param {string} propertyName
   * @returns {Type|null}
   */
  getPropertyType(propertyName) {
    const prop = this.properties.get(propertyName);
    if (prop) return prop.type;
    if (this.indexSignature) return this.indexSignature.valueType;
    return null;
  }

  /**
   * Check for excess properties against a target type.
   * Unlike isCompatibleWith (which allows extras for structural subtyping),
   * this is used at direct assignment sites (object literals) where excess
   * properties are an error, matching TypeScript's excess property checking.
   * @param {ObjectType} target
   * @returns {string[]} Array of excess property names
   */
  excessPropertiesAgainst(target) {
    if (!(target instanceof ObjectType)) return [];
    // If target has an index signature, any string key is valid — no excess properties
    if (target.indexSignature) return [];
    const excess = [];
    for (const key of this.properties.keys()) {
      if (!target.properties.has(key)) {
        excess.push(key);
      }
    }
    return excess;
  }
}

/**
 * Record types: Record<K, V> — an object with an open index signature where
 * every key is of type K (typically `string` or `number`) and every value
 * is of type V.  This is the structured equivalent of `{ [key: K]: V }`.
 */
export class RecordType extends Type {
  constructor(keyType, valueType) {
    super();
    this.kind = 'record';
    this.keyType = keyType;     // typically StringType or NumberType
    this.valueType = valueType; // value type, e.g. AnyType or StringType
  }

  toString() {
    return `Record<${this.keyType.toString()}, ${this.valueType.toString()}>`;
  }

  equals(other) {
    return (
      other instanceof RecordType &&
      this.keyType.equals(other.keyType) &&
      this.valueType.equals(other.valueType)
    );
  }

  isCompatibleWith(target, aliases) {
    if (target instanceof PrimitiveType && target.name === 'any') return true;

    // Resolve aliases and type member access
    if (target instanceof TypeAlias || target instanceof TypeMemberAccess || target instanceof KeyofType) {
      const resolved = aliases.resolve(target);
      if (resolved === target) return false;
      return this.isCompatibleWith(resolved, aliases);
    }

    // A Record<K,V> is compatible with another Record<K,V> if value types are compatible
    if (target instanceof RecordType) {
      return (
        this.keyType.isCompatibleWith(target.keyType, aliases) &&
        this.valueType.isCompatibleWith(target.valueType, aliases)
      );
    }

    // A Record<string, V> is compatible with an ObjectType if all its known
    // required property value types are compatible with V
    if (target instanceof ObjectType) {
      for (const [, prop] of target.properties) {
        if (!prop.optional && !this.valueType.isCompatibleWith(prop.type, aliases)) {
          return false;
        }
      }
      return true;
    }

    // Check union
    if (target instanceof UnionType) {
      return target.types.some(t => this.isCompatibleWith(t, aliases));
    }

    return false;
  }

  /**
   * Look up the value type for a given key (always returns valueType for records).
   * @returns {Type}
   */
  getValueType() {
    return this.valueType;
  }
}

/**
 * Union types: string | number
 */
export class UnionType extends Type {
  constructor(types) {
    super();
    this.kind = 'union';
    this.types = this._normalizeTypes(types);
  }
  
  _normalizeTypes(types) {
    // Remove duplicates and flatten nested unions
    const flattened = [];
    const seen = new Set();
    
    for (const type of types) {
      if (type instanceof UnionType) {
        // Flatten nested unions
        flattened.push(...type.types);
      } else if (type instanceof PrimitiveType && type.name === 'any') {
        // any absorbs everything
        return [Types.any];
      } else if (!(type instanceof PrimitiveType && type.name === 'never')) {
        // Skip never, don't add duplicates
        const str = type.toString();
        if (!seen.has(str)) {
          seen.add(str);
          flattened.push(type);
        }
      }
    }
    
    // Simplify: if base type is present, remove its literals
    const hasString = flattened.some(t => t instanceof PrimitiveType && t.name === 'string');
    const hasNumber = flattened.some(t => t instanceof PrimitiveType && t.name === 'number');
    const hasBoolean = flattened.some(t => t instanceof PrimitiveType && t.name === 'boolean');
    
    if (hasString || hasNumber || hasBoolean) {
      return flattened.filter(t => {
        if (t instanceof LiteralType) {
          if (t.baseType === StringType && hasString) return false;
          if (t.baseType === NumberType && hasNumber) return false;
          if (t.baseType === BooleanType && hasBoolean) return false;
        }
        return true;
      });
    }
    
    return flattened;
  }
  
  toString() {
    if (this.types.length === 0) return 'never';
    if (this.types.length === 1) return this.types[0].toString();
    return this.types.map(t => t.toString()).join(' | ');
  }
  
  equals(other) {
    if (!(other instanceof UnionType)) return false;
    if (this.types.length !== other.types.length) return false;
    
    // Check if all types are present (order independent)
    const thisStrs = new Set(this.types.map(t => t.toString()));
    const otherStrs = new Set(other.types.map(t => t.toString()));
    
    for (const str of thisStrs) {
      if (!otherStrs.has(str)) return false;
    }
    
    return true;
  }
  
  isCompatibleWith(target, aliases) {
    if (target instanceof PrimitiveType && target.name === 'any') return true;
    
    // Resolve aliases and type member access
    if (target instanceof TypeAlias || target instanceof TypeMemberAccess || target instanceof KeyofType) {
      const resolved = aliases.resolve(target);
      // Avoid infinite recursion if alias can't be resolved
      if (resolved === target) return false;
      return this.isCompatibleWith(resolved, aliases);
    }

    // When target is also a union, each type in this union must be covered by
    // at least one type in the target union — don't compare each constituent
    // against the whole union object, which would fail for e.g. FunctionType.
    if (target instanceof UnionType) {
      return this.types.every(t => target.types.some(targetT => t.isCompatibleWith(targetT, aliases)));
    }
    
    // Union is compatible if all constituent types are compatible with the single target
    return this.types.every(t => t.isCompatibleWith(target, aliases));
  }
  
  /**
   * Remove null and undefined from the union
   * @returns {Type}
   */
  removeNullish() {
    const filtered = this.types.filter(t => 
      !(t instanceof PrimitiveType && (t.name === 'null' || t.name === 'undefined'))
    );
    
    if (filtered.length === 0) return NeverType;
    if (filtered.length === 1) return filtered[0];
    return new UnionType(filtered);
  }
  
  /**
   * Narrow union to only specified type
   * @param {Type} narrowedType
   * @returns {Type}
   */
  narrow(narrowedType) {
    const filtered = this.types.filter(t => t.equals(narrowedType));
    
    if (filtered.length === 0) return Types.never;
    if (filtered.length === 1) return filtered[0];
    return new UnionType(filtered);
  }
  
  /**
   * Remove specified type from union
   * @param {Type} excludedType
   * @returns {Type}
   */
  exclude(excludedType) {
    const filtered = this.types.filter(t => !t.equals(excludedType));
    
    if (filtered.length === 0) return Types.never;
    if (filtered.length === 1) return filtered[0];
    return new UnionType(filtered);
  }
}

/**
 * Intersection types: A & B
 *
 * Semantics: a value of intersection type A & B satisfies *both* A and B.
 * For object types this means the merged set of all properties from every
 * constituent type.  For non-object types it means the value must satisfy
 * every constituent independently.
 */
export class IntersectionType extends Type {
  constructor(types) {
    super();
    this.kind = 'intersection';
    this.types = types;
    // Cache the merged object type so it is computed once per instance.
    this._merged = null;
  }

  /**
   * If every constituent is an ObjectType (or resolves to one via aliases),
   * return a single ObjectType whose properties are the union of all
   * constituent properties.  Later properties override earlier ones when
   * names collide (matching TypeScript's intersection merge semantics for
   * same-named primitive properties – keep both for object-typed ones in a
   * real implementation; here we keep the last declaration for simplicity).
   *
   * @param {TypeAliasMap|null} aliases
   * @returns {ObjectType|null}  null when any constituent is not an ObjectType
   */
  merge(aliases) {
    if (this._merged) return this._merged;

    const merged = new Map();
    for (const t of this.types) {
      const resolved = aliases ? aliases.resolve(t) : t;
      if (!(resolved instanceof ObjectType)) {
        return null; // Can't merge – at least one constituent is non-object
      }
      for (const [key, prop] of resolved.properties) {
        merged.set(key, prop);
      }
    }

    this._merged = new ObjectType(merged);
    return this._merged;
  }

  toString() {
    return this.types.map(t => t.toString()).join(' & ');
  }

  equals(other) {
    if (!(other instanceof IntersectionType)) return false;
    if (this.types.length !== other.types.length) return false;

    // Order-independent comparison
    const thisStrs = new Set(this.types.map(t => t.toString()));
    const otherStrs = new Set(other.types.map(t => t.toString()));

    for (const str of thisStrs) {
      if (!otherStrs.has(str)) return false;
    }

    return true;
  }

  isCompatibleWith(target, aliases) {
    if (target instanceof PrimitiveType && target.name === 'any') return true;

    // Resolve aliases and type member access on the target side
    if (target instanceof TypeAlias || target instanceof TypeMemberAccess || target instanceof KeyofType) {
      const resolved = aliases.resolve(target);
      if (resolved === target) return false; // Unresolvable alias
      return this.isCompatibleWith(resolved, aliases);
    }

    // For all-object intersections delegate to the merged ObjectType so that
    // property presence and types are checked correctly (structural subtyping).
    const merged = this.merge(aliases);
    if (merged) {
      return merged.isCompatibleWith(target, aliases);
    }

    // For mixed/non-object intersections: the value satisfies *all* constituents,
    // so it is compatible with target if at least one constituent is compatible.
    // This covers cases like `string & Serializable` being compatible with `string`.
    return this.types.some(t => t.isCompatibleWith(target, aliases));
  }
}

/**
 * Generic type instantiation: Box<T>
 */
export class GenericType extends Type {
  constructor(baseType, typeArgs) {
    super();
    this.kind = 'generic';
    this.baseType = baseType; // Type or string (name)
    this.typeArgs = typeArgs; // Type[]
  }
  
  toString() {
    const baseStr = typeof this.baseType === 'string' ? this.baseType : this.baseType.toString();
    const argsStr = this.typeArgs.map(t => t.toString()).join(', ');
    return `${baseStr}<${argsStr}>`;
  }
  
  equals(other) {
    if (!(other instanceof GenericType)) return false;
    
    const baseEqual = typeof this.baseType === 'string' 
      ? this.baseType === other.baseType
      : this.baseType.equals(other.baseType);
    
    if (!baseEqual) return false;
    if (this.typeArgs.length !== other.typeArgs.length) return false;
    
    return this.typeArgs.every((arg, i) => arg.equals(other.typeArgs[i]));
  }
  
  isCompatibleWith(target, aliases) {
    if (target instanceof PrimitiveType && target.name === 'any') return true;
    
    // Resolve aliases and type member access
    if (target instanceof TypeAlias || target instanceof TypeMemberAccess || target instanceof KeyofType) {
      const resolved = aliases.resolve(target);
      // Avoid infinite recursion if alias can't be resolved
      if (resolved === target) return false;
      return this.isCompatibleWith(resolved, aliases);
    }
    
    // Check if both are the same generic type with compatible args
    if (target instanceof GenericType) {
      const baseEqual = typeof this.baseType === 'string' 
        ? this.baseType === target.baseType
        : this.baseType.equals(target.baseType);
      
      if (!baseEqual) return false;
      if (this.typeArgs.length !== target.typeArgs.length) return false;
      
      return this.typeArgs.every((arg, i) => 
        arg.isCompatibleWith(target.typeArgs[i], aliases)
      );
    }
    
    return false;
  }
}

/**
 * Function types: (a: string, b: number) => boolean
 */
export class FunctionType extends Type {
  constructor(params, returnType, genericParams = [], paramNames = [], paramHasDefault = null, genericConstraints = null) {
    super();
    this.kind = 'function';
    this.params = params; // Type[]
    this.returnType = returnType; // Type
    this.genericParams = genericParams; // string[]
    this.paramNames = paramNames; // string[]
    this.paramHasDefault = paramHasDefault; // boolean[] | null
    this.genericConstraints = genericConstraints; // Map<string, Type> | null
    this.funcName = null; // string | null — set by inference for error messages
  }
  
  toString() {
    const generics = this.genericParams.length > 0
      ? `<${this.genericParams.map(p => {
          const c = this.genericConstraints?.get(p);
          return c ? `${p} extends ${c.toString()}` : p;
        }).join(', ')}>`
      : '';
    let params = '';
    if (this.params) {
      params = this.params.map((p, i) => {
        const paramName = this.paramNames && this.paramNames[i] ? this.paramNames[i] : `p${i}`;
        return `${paramName}: ${p.toString()}`;
      }).join(', ');
    }
    return `${generics}(${params}) => ${this.returnType.toString()}`;
  }
  
  equals(other) {
    if (!(other instanceof FunctionType)) return false;
    // AnyFunctionType equals any FunctionType
    if (this.params === null || other.params === null) return true;
    if (this.params.length !== other.params.length) return false;
    if (!this.returnType.equals(other.returnType)) return false;
    
    return this.params.every((param, i) => param.equals(other.params[i]));
  }
  
  isCompatibleWith(target, aliases) {
    if (target instanceof PrimitiveType && target.name === 'any') return true;
    
    // Resolve aliases and type member access
    if (target instanceof TypeAlias || target instanceof TypeMemberAccess || target instanceof KeyofType) {
      const resolved = aliases.resolve(target);
      // Avoid infinite recursion if alias can't be resolved
      if (resolved === target) return false;
      return this.isCompatibleWith(resolved, aliases);
    }

    // A function type is assignable to a union if it is assignable to any member
    // (e.g. a concrete function is valid for `function | null`)
    if (target instanceof UnionType) {
      return target.types.some(t => this.isCompatibleWith(t, aliases));
    }
    
    // Simple function compatibility (contravariant params, covariant return)
    if (target instanceof FunctionType) {
      // AnyFunctionType (marker with null params) accepts any function
      if (target.params === null || this.params === null) return true;
      if (this.params.length !== target.params.length) return false;
      
      // Parameters are contravariant
      const paramsCompatible = target.params.every((targetParam, i) =>
        targetParam.isCompatibleWith(this.params[i], aliases)
      );
      
      // Return type is covariant.
      // 'void' as the target return type means "we don't use the return value",
      // so any actual return type (including undefined, Promise<...>, etc.) is fine.
      const targetReturnIsVoid = target.returnType instanceof PrimitiveType && target.returnType.name === 'void';
      const returnCompatible = targetReturnIsVoid || this.returnType.isCompatibleWith(target.returnType, aliases);
      
      return paramsCompatible && returnCompatible;
    }
    
    return false;
  }
}

/**
 * Singleton representing a function with unknown signature (used as a type marker)
 */
export const AnyFunctionType = new FunctionType(null, AnyType);

/**
 * Predicate types: `x is string` in a function return annotation.
 * A predicate function returns boolean at runtime, but the type system uses this
 * class to carry the narrowing contract: "if this function returns true, the
 * argument named `paramName` has type `guardType`".
 */
export class PredicateType extends Type {
  /**
   * @param {string} paramName - Name of the guarded parameter (e.g. 'x' in 'x is string')
   * @param {Type} guardType   - The narrowed type (e.g. StringType for 'x is string')
   */
  constructor(paramName, guardType) {
    super();
    this.kind = 'predicate';
    this.paramName = paramName;
    this.guardType = guardType;
  }

  toString() {
    return `${this.paramName} is ${this.guardType}`;
  }

  equals(other) {
    return (
      other instanceof PredicateType &&
      this.paramName === other.paramName &&
      this.guardType.equals(other.guardType)
    );
  }

  isCompatibleWith(target, aliases) {
    // A predicate type is a subtype of boolean (and of any)
    if (target instanceof PrimitiveType && (target.name === 'any' || target.name === 'boolean')) return true;
    if (target instanceof UnionType) return target.types.some(t => this.isCompatibleWith(t, aliases));
    if (target instanceof PredicateType) return this.guardType.isCompatibleWith(target.guardType, aliases);
    return false;
  }
}

/**
 * Keyof type: `keyof T` — the union of all known property-name types for T.
 *
 * For an ObjectType with properties {a, b, c} it resolves to `"a" | "b" | "c"`.
 * For a RecordType<K, V> it resolves to K.
 * For `any` it resolves to `string | number`.
 */
export class KeyofType extends Type {
  /**
   * @param {Type} subjectType - The type whose keys are extracted (usually a TypeAlias)
   */
  constructor(subjectType) {
    super();
    this.kind = 'keyof';
    this.subjectType = subjectType;
  }

  toString() {
    return `keyof ${this.subjectType.toString()}`;
  }

  equals(other) {
    return other instanceof KeyofType && this.subjectType.equals(other.subjectType);
  }

  /**
   * Eagerly resolve this KeyofType to a concrete union (or primitive) type.
   * @param {TypeAliasMap} aliases
   * @returns {Type}
   */
  resolve(aliases) {
    return aliases.resolveKeyof(this);
  }

  isCompatibleWith(target, aliases) {
    const resolved = this.resolve(aliases);
    return resolved.isCompatibleWith(target, aliases);
  }
}

/**
 * Type member access: resolve a named property of a type alias at check time.
 * Represents syntax like `State.dogPage` or `State['dogPage']`.
 */
export class TypeMemberAccess extends Type {
  /**
   * @param {Type} baseType - The base type (usually a TypeAlias)
   * @param {string} memberKey - The property name being accessed
   */
  constructor(baseType, memberKey) {
    super();
    this.kind = 'member_access';
    this.baseType = baseType;
    this.memberKey = memberKey;
  }

  toString() {
    return `${this.baseType.toString()}['${this.memberKey}']`;
  }

  equals(other) {
    return (
      other instanceof TypeMemberAccess &&
      this.baseType.equals(other.baseType) &&
      this.memberKey === other.memberKey
    );
  }

  isCompatibleWith(target, aliases) {
    const resolved = aliases.resolveMemberAccess(this);
    if (resolved === this) {
      // Could not resolve — treat as any
      return target instanceof PrimitiveType && target.name === 'any';
    }
    return resolved.isCompatibleWith(target, aliases);
  }
}

/**
 * Type alias reference (not resolved yet)
 */
export class TypeAlias extends Type {
  constructor(name) {
    super();
    this.kind = 'alias';
    this.name = name;
  }
  
  toString() {
    return this.name;
  }
  
  equals(other) {
    return other instanceof TypeAlias && this.name === other.name;
  }
  
  isCompatibleWith(target, aliases) {
    // Always resolve aliases before checking compatibility
    const resolved = aliases.resolve(this);
    
    // If resolution didn't change the type (circular reference), check by name
    if (resolved instanceof TypeAlias && resolved.name === this.name) {
      if (target instanceof UnionType) {
        if (target.types.some(t => t instanceof TypeAlias && t.name === this.name)) {
          return true;
        }
      }
      if (this.name === 'array') {
        if (target instanceof ArrayType) return true;
        if (target instanceof TypeAlias && target.name === 'array') return true;
        if (target instanceof UnionType) {
          return target.types.some(t => this.isCompatibleWith(t, aliases));
        }
      }
      if (this.name === 'object') {
        if (target instanceof ObjectType) return true;
        if (target instanceof TypeAlias && target.name === 'object') return true;
        if (target instanceof UnionType) {
          return target.types.some(t => this.isCompatibleWith(t, aliases));
        }
      }
      // Circular alias - check if target is the same alias
      if (target instanceof TypeAlias && target.name === this.name) {
        return true;
      }
      // Otherwise, treat as 'any' to avoid errors
      return target instanceof PrimitiveType && target.name === 'any';
    }
    
    return resolved.isCompatibleWith(target, aliases);
  }
}

/**
 * Type alias map - stores and resolves type aliases
 */
export class TypeAliasMap {
  constructor() {
    this.aliases = new Map(); // Map<string, Type | {type: Type, genericParams: string[]}>
    this._resolving = new Set(); // Track currently resolving aliases to prevent cycles
  }
  
  /**
   * Define a type alias
   * @param {string} name
   * @param {Type} type
   * @param {string[]} genericParams
   * @param {Map<string, Type>|null} genericConstraints
   */
  define(name, type, genericParams = [], genericConstraints = null) {
    if (genericParams.length > 0) {
      this.aliases.set(name, { type, genericParams, genericConstraints: genericConstraints?.size > 0 ? genericConstraints : null });
    } else {
      this.aliases.set(name, type);
    }
  }
  
  /**
   * Check if an alias exists
   * @param {string} name
   * @returns {boolean}
   */
  has(name) {
    return this.aliases.has(name);
  }
  
  /**
   * Get alias definition (unresolved)
   * @param {string} name
   * @returns {Type | {type: Type, genericParams: string[]}}
   */
  get(name) {
    return this.aliases.get(name);
  }
  
  /**
   * Resolve a TypeMemberAccess by looking up the base alias and extracting the property.
   * @param {TypeMemberAccess} type
   * @returns {Type} Resolved property type, or the original TypeMemberAccess if unresolvable
   */
  resolveMemberAccess(type) {
    const resolvedBase = this.resolve(type.baseType);
    if (resolvedBase instanceof ObjectType) {
      const prop = resolvedBase.properties.get(type.memberKey);
      if (prop) return this.resolve(prop.type);
    }
    // Base resolved to something other than ObjectType — cannot extract property
    return type;
  }

  /**
   * Resolve a KeyofType to a concrete union of its subject's property-name types.
   * @param {KeyofType} type
   * @returns {Type}
   */
  resolveKeyof(type) {
    const resolved = this.resolve(type.subjectType);
    if (resolved instanceof ObjectType) {
      const keys = [...resolved.properties.keys()];
      if (keys.length === 0) return NeverType;
      return createUnion(keys.map(k => new LiteralType(k, StringType)));
    }
    if (resolved instanceof RecordType) {
      return resolved.keyType;
    }
    if (resolved instanceof PrimitiveType && resolved.name === 'any') {
      return new UnionType([StringType, NumberType]);
    }
    // Unknown/unresolvable subject — fall back to string
    return StringType;
  }

  /**
   * Resolve a type alias (recursive with cycle detection)
   * @param {Type} type
   * @returns {Type}
   */
  resolve(type) {
    if (type instanceof TypeMemberAccess) {
      return this.resolveMemberAccess(type);
    }
    if (type instanceof KeyofType) {
      return this.resolveKeyof(type);
    }
    if (!(type instanceof TypeAlias)) {
      return type;
    }
    
    // Check for cycles
    if (this._resolving.has(type.name)) {
      // Circular reference detected - return as-is to break the cycle
      return type;
    }
    
    const aliasValue = this.aliases.get(type.name);
    
    if (!aliasValue) {
      // Unknown alias - return as-is
      return type;
    }
    
    // Check if it's a generic alias (should not be used without type args)
    if (typeof aliasValue === 'object' && aliasValue.genericParams && aliasValue.genericParams.length > 0) {
      // Generic type used without type arguments - return as-is
      return type;
    }
    
    // Mark this alias as currently being resolved
    this._resolving.add(type.name);
    
    try {
      // Recursively resolve
      return this.resolve(aliasValue);
    } finally {
      // Clean up after resolution
      this._resolving.delete(type.name);
    }
  }
  
  /**
   * Instantiate a generic type alias
   * @param {string} name - Alias name
   * @param {Type[]} typeArgs - Type arguments
   * @returns {Type}
   */
  instantiate(name, typeArgs) {
    const aliasValue = this.aliases.get(name);
    
    if (!aliasValue || !aliasValue.genericParams) {
      // Not a generic alias
      return new TypeAlias(name);
    }
    
    // Create substitution map
    const substitutions = new Map();
    for (let i = 0; i < aliasValue.genericParams.length && i < typeArgs.length; i++) {
      substitutions.set(aliasValue.genericParams[i], typeArgs[i]);
    }
    
    // Substitute type parameters
    return substituteTypeParams(aliasValue.type, substitutions);
  }
}

/**
 * Substitute type parameters in a type
 * @param {Type} type
 * @param {Map<string, Type>} substitutions
 * @returns {Type}
 */
export function substituteTypeParams(type, substitutions) {
  if (type instanceof TypeAlias) {
    // Check if this is a type parameter
    if (substitutions.has(type.name)) {
      return substitutions.get(type.name);
    }
    return type;
  }
  
  if (type instanceof ArrayType) {
    return new ArrayType(substituteTypeParams(type.elementType, substitutions));
  }
  
  if (type instanceof TupleType) {
    return new TupleType(type.elements.map(el => substituteTypeParams(el, substitutions)));
  }
  
  if (type instanceof ObjectType) {
    const newProps = new Map();
    for (const [key, prop] of type.properties) {
      newProps.set(key, {
        type: substituteTypeParams(prop.type, substitutions),
        optional: prop.optional
      });
    }
    const newIndexSig = type.indexSignature ? {
      keyType: substituteTypeParams(type.indexSignature.keyType, substitutions),
      valueType: substituteTypeParams(type.indexSignature.valueType, substitutions),
    } : null;
    return new ObjectType(newProps, type.name, newIndexSig);
  }
  
  if (type instanceof UnionType) {
    return new UnionType(type.types.map(t => substituteTypeParams(t, substitutions)));
  }
  
  if (type instanceof IntersectionType) {
    return new IntersectionType(type.types.map(t => substituteTypeParams(t, substitutions)));
  }
  
  if (type instanceof GenericType) {
    return new GenericType(
      type.baseType,
      type.typeArgs.map(t => substituteTypeParams(t, substitutions))
    );
  }
  
  if (type instanceof FunctionType) {
    if (type.params === null) return type; // AnyFunctionType — no substitution needed
    const ft = new FunctionType(
      type.params.map(t => substituteTypeParams(t, substitutions)),
      substituteTypeParams(type.returnType, substitutions),
      type.genericParams,
      type.paramNames,
      type.paramHasDefault,
      type.genericConstraints
    );
    ft.funcName = type.funcName;
    return ft;
  }

  if (type instanceof PredicateType) {
    return new PredicateType(
      type.paramName,
      substituteTypeParams(type.guardType, substitutions)
    );
  }

  if (type instanceof KeyofType) {
    return new KeyofType(substituteTypeParams(type.subjectType, substitutions));
  }

  // Primitives and literals don't need substitution
  return type;
}

/**
 * Create a union type, with automatic normalization
 * @param {Type[]} types
 * @returns {Type}
 */
export function createUnion(types) {
  if (types.length === 0) return NeverType;
  if (types.length === 1) return types[0];
  
  const union = new UnionType(types);
  if (union.types.length === 1) return union.types[0];
  return union;
}

/**
 * Helper to create common types
 */
export const Types = {
  any: AnyType,
  never: NeverType,
  string: StringType,
  number: NumberType,
  boolean: BooleanType,
  null: NullType,
  undefined: UndefinedType,
  void: VoidType,
  
  literal(value, baseType) {
    return new LiteralType(value, baseType);
  },
  
  array(elementType) {
    return new ArrayType(elementType);
  },
  
  object(properties, indexSignature = null) {
    return new ObjectType(properties, null, indexSignature);
  },
  
  union(types) {
    return createUnion(types);
  },
  
  intersection(types) {
    return new IntersectionType(types);
  },
  
  generic(baseType, typeArgs) {
    return new GenericType(baseType, typeArgs);
  },
  
  function(params, returnType, genericParams = [], paramNames = []) {
    return new FunctionType(params, returnType, genericParams, paramNames);
  },
  
  alias(name) {
    return new TypeAlias(name);
  },

  record(keyType, valueType) {
    return new RecordType(keyType, valueType);
  },

  tuple(elements) {
    return new TupleType(elements);
  },

  predicate(paramName, guardType) {
    return new PredicateType(paramName, guardType);
  },

  keyof(subjectType) {
    return new KeyofType(subjectType);
  }
};
